import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useCallback } from 'react';
import { Transition } from 'react-transition-group';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { duration } from '../../../themes/theme-keys/transitions.esm.js';
import { reflow, getTransitionProps } from '../transitions/utils.esm.js';

var _excluded = ["addEndListener", "appear", "children", "easing", "in", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "style", "timeout", "TransitionComponent"];
var styles = {
  entering: {
    opacity: 1
  },
  entered: {
    opacity: 1
  }
};
var defaultTimeout = {
  enter: duration.enteringScreen,
  exit: duration.leavingScreen
};

/**
 * The Fade transition is used by the [Modal](/components/modal/) component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
var Fade = /*#__PURE__*/React.forwardRef(function Fade(props, ref) {
  var addEndListener = props.addEndListener,
    _props$appear = props.appear,
    appear = _props$appear === void 0 ? true : _props$appear,
    children = props.children,
    easing = props.easing,
    inProp = props["in"],
    onEnter = props.onEnter,
    onEntered = props.onEntered,
    onEntering = props.onEntering,
    onExit = props.onExit,
    onExited = props.onExited,
    onExiting = props.onExiting,
    style = props.style,
    _props$timeout = props.timeout,
    timeout = _props$timeout === void 0 ? defaultTimeout : _props$timeout,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Transition : _props$TransitionComp,
    other = _objectWithoutProperties(props, _excluded);
  var theme = useSafeThemeUI(props);
  var nodeRef = useRef(null);
  //@ts-ignore
  var foreignRef = useForkRef(children === null || children === void 0 ? void 0 : children.ref, ref);
  var handleRef = useForkRef(nodeRef, foreignRef);
  var normalizedTransitionCallback = function normalizedTransitionCallback(callback) {
    return function (isAppearing) {
      if (callback) {
        var node = nodeRef.current;
        if (node) {
          // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
          if (isAppearing === undefined) {
            // @ts-ignore
            callback(node);
          } else {
            callback(node, !!isAppearing);
          }
        }
      }
    };
  };
  var handleEntering = normalizedTransitionCallback(onEntering);
  var handleEnter = normalizedTransitionCallback(function (node, isAppearing) {
    reflow(node); // So the animation always start from the start.

    var transitionProps = getTransitionProps({
      style: style,
      timeout: timeout,
      easing: easing
    }, {
      mode: 'enter'
    });
    node.style.webkitTransition = theme.transitions ? theme.transitions.create('opacity', transitionProps) : 'opacity';
    node.style.transition = theme.transitions ? theme.transitions.create('opacity', transitionProps) : 'opacity';
    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });
  var handleEntered = normalizedTransitionCallback(onEntered);
  var handleExiting = normalizedTransitionCallback(onExiting);
  var handleExit = normalizedTransitionCallback(function (node) {
    var transitionProps = getTransitionProps({
      style: style,
      timeout: timeout,
      easing: easing
    }, {
      mode: 'exit'
    });
    node.style.webkitTransition = theme.transitions ? theme.transitions.create('opacity', transitionProps) : 'opacity';
    node.style.transition = theme.transitions ? theme.transitions.create('opacity', transitionProps) : 'opacity';
    if (onExit) {
      onExit(node);
    }
  });
  var handleExited = normalizedTransitionCallback(onExited);
  var handleAddEndListener = useCallback(function (next) {
    var element = nodeRef.current;
    if (addEndListener && element) {
      // Old call signature before `react-transition-group` implemented `nodeRef`
      addEndListener(element, next);
    }
  }, [addEndListener]);
  return /*#__PURE__*/React.createElement(TransitionComponent, _extends({
    appear: appear,
    "in": inProp,
    nodeRef: nodeRef ,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting,
    addEndListener: handleAddEndListener,
    timeout: timeout
  }, other), function (state, childProps) {
    return children ? /*#__PURE__*/React.cloneElement(children, _objectSpread2({
      style: _objectSpread2(_objectSpread2(_objectSpread2({
        opacity: 0,
        visibility: state === 'exited' && !inProp ? 'hidden' : undefined
      }, styles[state]), style), children.props.style),
      ref: handleRef
    }, childProps)) : null;
  });
});

export default Fade;
//# sourceMappingURL=fade.esm.js.map
